import React, { useContext, useEffect, useState } from 'react';
import { getGames, deleteGame, toggleGameActive } from '../../api/adminApi';
import common from '../../common.module.scss';
import ConfirmDeleteModal from '../ConfirmDeleteModal.jsx';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Pagination from '../pagination';
import { EmployeeContext } from '../../context/EmployeeContext.jsx';
import useIsMobile from "@/shared/lib/hooks/useIsMobile";
import { GoPencil } from "react-icons/go";
import { RiDeleteBinLine } from "react-icons/ri";
import { GiSettingsKnobs } from "react-icons/gi";
import FilterModal from "@/shared/ui/FiltersModal";

export default function GameTable() {
    const { employee } = useContext(EmployeeContext);
    const role = employee?.role;

    const [games, setGames] = useState([]);
    const [, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [direction, setDirection] = useState('asc');
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [gameToDelete, setGameToDelete] = useState(null);
    const [showModalFilters, setShowModalFilters] = useState(false);

    const navigate = useNavigate();
    const isMobile = useIsMobile();

    useEffect(() => {
        loadGames();
    }, [page, search, sortBy, direction]);

    const loadGames = async () => {
        setLoading(true);
        try {
            const params = {
                page,
                size: 10,
                ...(search && { search }),
                ...(sortBy && { sortBy }),
                ...(sortBy && { direction })
            };
            const res = await getGames(params);
            const data = res.data.content || res.data;
            setGames(data.map(item => ({
                id: item.id,
                title: item.title,
                system: item.gameSystemName,
                category: item.sessionFormatName,
                active: item.isActive
            })));
            setTotalPages(res.data.totalPages || 1);
        } catch (e) {
            const errorMessage = e?.response?.data?.message || 'Ошибка при загрузке игр';
            toast.error(errorMessage);
            setError('Ошибка при загрузке игр');
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field) => {
        if (sortBy !== field) {
            setSortBy(field);
            setDirection('desc');
        } else if (direction === 'desc') {
            setDirection('asc');
        } else {
            setSortBy('');
            setDirection('asc'); // сброс
        }
        setPage(0);
    };

    const renderSortArrow = (field) => {
        if (sortBy !== field) return '';
        return direction === 'asc' ? ' ↑' : ' ↓';
    };

    const handleDelete = async () => {
        if (!gameToDelete) return;
        try {
            await deleteGame(gameToDelete.id);
            setGames(prev => prev.filter(g => g.id !== gameToDelete.id));
        } catch (e) {
            const errorMessage = e?.response?.data?.message || 'Ошибка при удалении игры';
            toast.error(errorMessage);
        } finally {
            setGameToDelete(null);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setPage(newPage);
        }
    };

    const handleToggleActive = async (gameId) => {
        try {
            await toggleGameActive(gameId);
            setGames(prev =>
                prev.map(g =>
                    g.id === gameId ? { ...g, active: !g.active } : g
                )
            );
        } catch (e) {
            const errorMessage = e?.response?.data?.message || 'Ошибка при смене активности';
            toast.error(errorMessage);
        }
    };

    // сортировка через мобильную модалку
    const handleChangeSort = (field, dir) => {
        setSortBy(field);
        setDirection(dir);
        setPage(0);
    };

    const sortOptions = [
        { value: 'id',                 label: 'ID' },
        { value: 'title',              label: 'Название' },
        { value: 'gameSystemName',     label: 'Система' },
        { value: 'sessionFormatName',  label: 'Категория' },
        { value: 'isActive',           label: 'Состояние' },
    ];

    if (error) return <h2>{error}</h2>;

    return (
        <div className={`container-fluid ${common.container}`}>
            {isMobile && (<h1 className={common.mobileHeading}>Карточки игр</h1>)}

            <div className="d-flex align-items-center mb-3">
                <div className="col-md-6 col-10">
                    <input
                        type="text"
                        placeholder="Поиск"
                        className={`form-control ${common.searchInput}`}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                    />
                </div>

                {/* Кнопка сортировки на мобилке */}
                {isMobile && (
                    <div className="col-2 d-flex justify-content-end">
                        <button
                            type="button"
                            className={`${common.iconButton} ms-2`}
                            onClick={() => setShowModalFilters(true)}
                            aria-label="Открыть сортировку"
                        >
                            <GiSettingsKnobs size={20} />
                        </button>
                    </div>
                )}

                {role !== "ADMIN" && role !== "MASTER" && !isMobile && (
                    <button
                        className="service-btn col-auto ms-3"
                        onClick={() => navigate('/admin/games/create')}
                    >
                        Добавить игру
                    </button>
                )}
            </div>

            <table className={`table ${common.table}`}>
                <thead className={common.tableHeader}>
                <tr>
                    <th className={common.sortable} onClick={() => handleSort('id')}>
                        №{renderSortArrow('id')}
                    </th>
                    <th className={common.sortable} onClick={() => handleSort('title')}>
                        {isMobile ? "Игра" : "Название"}{renderSortArrow('title')}
                    </th>
                    {!isMobile && (
                        <>
                            <th className={common.sortable} onClick={() => handleSort('gameSystemName')}>
                                Система{renderSortArrow('gameSystemName')}
                            </th>
                            <th className={common.sortable} onClick={() => handleSort('sessionFormatName')}>
                                Категория{renderSortArrow('sessionFormatName')}
                            </th>
                        </>
                    )}
                    <th className={common.sortable} onClick={() => handleSort('isActive')}>
                        {isMobile ? "" : "Состояние"}{renderSortArrow('isActive')}
                    </th>
                    {(role !== "ADMIN" && role !== "MASTER") && (
                        <>
                            <th></th>
                            <th></th>
                        </>
                    )}
                </tr>
                </thead>

                <tbody>
                {games.map((game, index) => (
                    <tr key={game.id} className={index % 2 === 1 ? common.altRow : ''}>
                        <td className="align-middle">{game.id}</td>
                        <td>
                            {game.title}
                            {isMobile && (
                                <>
                                    <p>{game.system}</p>
                                    <p className="opacity-50">{game.category}</p>
                                </>
                            )}
                        </td>
                        {!isMobile && (
                            <>
                                <td>{game.system}</td>
                                <td>{game.category}</td>
                            </>
                        )}
                        <td className="align-middle">
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={game.active}
                                    onChange={() => handleToggleActive(game.id)}
                                />
                            </div>
                        </td>
                        {role !== "ADMIN" && role !== "MASTER" && (
                            <>
                                <td className="align-middle">
                                    <button
                                        className={`btn-word black btn-link me-md-3 ${common.editBtn}`}
                                        onClick={() => navigate(`/admin/games/edit/${game.id}`)}
                                    >
                                        {isMobile ? <GoPencil className={common.icon}/> : "Редактировать"}
                                    </button>
                                </td>
                                <td className="align-middle">
                                    <button
                                        className={`btn-word red btn-link ${common.editBtn}`}
                                        onClick={() => setGameToDelete(game)}
                                    >
                                        {isMobile ? <RiDeleteBinLine className={common.icon}/> : "Удалить"}
                                    </button>
                                </td>
                            </>
                        )}
                    </tr>
                ))}
                </tbody>
            </table>

            <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />

            {role !== "ADMIN" && role !== "MASTER" && isMobile && (
                <div className="col-12">
                    <button
                        className="service-btn col-auto w-100"
                        onClick={() => navigate('/admin/games/create')}
                    >
                        Добавить игру
                    </button>
                </div>
            )}

            {gameToDelete && (
                <ConfirmDeleteModal
                    roomName={gameToDelete.title}
                    onConfirm={handleDelete}
                    onCancel={() => setGameToDelete(null)}
                />
            )}

            <FilterModal
                isOpen={showModalFilters}
                onClose={() => setShowModalFilters(false)}
                sortOptions={sortOptions}
                sortBy={sortBy}
                direction={direction}
                onChangeSort={handleChangeSort}
            />
        </div>
    );
}
